import { useEffect, useState } from 'react';
import axios from 'axios';
import { Clock3, Mail, ShieldCheck, User, X } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const transcriptLines = (transcript = '') => transcript.split('\n').map((line) => line.trim()).filter(Boolean);

export const TranscriptDialog = ({ sessionId, open, onOpenChange }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open || !sessionId) return;
    const load = async () => {
      setLoading(true); setError(''); setData(null);
      try {
        const response = await axios.get(`${API}/admin/conversations/${sessionId}/transcript`, { withCredentials: true });
        setData(response.data);
      } catch (requestError) {
        setError(requestError.response?.data?.detail || 'Transcript could not be loaded.');
      } finally { setLoading(false); }
    };
    load();
  }, [open, sessionId]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[92vh] max-w-4xl overflow-hidden border-[#D7D1C7] bg-[#FAF8F5] p-0 rounded-sm" data-testid="admin-transcript-modal">
        <DialogHeader className="border-b border-[#DDD7CD] bg-white px-6 py-5"><div className="flex items-start justify-between gap-4"><div><p className="text-[10px] font-semibold uppercase text-[#D26046]">Sensitive access recorded</p><DialogTitle className="mt-2 font-serif text-2xl text-[#1A1C18]">Conversation transcript</DialogTitle><DialogDescription className="mt-2 text-sm text-[#6B6E69]">Review the selected user conversation. This access is recorded in the administrator audit log.</DialogDescription></div></div></DialogHeader>
        <div className="max-h-[calc(92vh-96px)] overflow-y-auto p-6">
          {loading && <div className="h-72 animate-pulse bg-[#F0ECE5]" data-testid="admin-transcript-loading" />}
          {error && <div className="border-l-4 border-[#D26046] bg-[#FBEDE8] p-4 text-sm text-[#8A3B27]" data-testid="admin-transcript-error">{error}</div>}
          {data && <div className="space-y-8">
            <div className="grid gap-px overflow-hidden border border-[#DDD7CD] bg-[#DDD7CD] sm:grid-cols-4" data-testid="admin-transcript-metadata">
              <div className="bg-white p-4"><User className="h-4 w-4 text-[#2C5545]" /><p className="mt-3 text-xs text-[#8A8D8A]">User</p><p className="mt-1 text-sm font-semibold">{data.user.name || 'Unknown'}</p></div>
              <div className="bg-white p-4"><Mail className="h-4 w-4 text-[#2C5545]" /><p className="mt-3 text-xs text-[#8A8D8A]">Email</p><p className="mt-1 break-all text-sm font-semibold">{data.user.email || 'Unknown'}</p></div>
              <div className="bg-white p-4"><Clock3 className="h-4 w-4 text-[#2C5545]" /><p className="mt-3 text-xs text-[#8A8D8A]">Started</p><p className="mt-1 text-sm font-semibold">{new Date(data.session.started_at).toLocaleString()}</p></div>
              <div className="bg-white p-4"><ShieldCheck className="h-4 w-4 text-[#D26046]" /><p className="mt-3 text-xs text-[#8A8D8A]">Scenario</p><p className="mt-1 text-sm font-semibold">{data.scenario.title || 'Unknown'}</p></div>
            </div>
            <div className="space-y-3" data-testid="admin-transcript-content">
              {transcriptLines(data.session.transcript).map((line, index) => {
                const isUser = line.toLowerCase().startsWith('user:');
                return <div className={`max-w-[86%] border px-4 py-3 text-sm leading-relaxed rounded-sm ${isUser ? 'ml-auto border-[#B8CDC4] bg-[#E8F0EC]' : 'border-[#DDD7CD] bg-white'}`} key={`${line}-${index}`}><p className="mb-1 text-[10px] font-semibold uppercase text-[#8A8D8A]">{isUser ? data.user.name || 'User' : 'AI role'}</p><p>{line.replace(/^(User|AI):\s*/i, '')}</p></div>;
              })}
              {!data.session.transcript && <p className="py-20 text-center text-sm text-[#8A8D8A]">No transcript was saved for this conversation.</p>}
            </div>
          </div>}
        </div>
      </DialogContent>
    </Dialog>
  );
};
